import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  User,
  Calendar,
  ClipboardList,
  Dumbbell,
  Scale,
  Sparkles,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/cn';

interface ShowcaseSurface {
  to: string;
  /** Translation key root under `common:showcase.surfaces.<key>` */
  key: string;
  icon: typeof LayoutDashboard;
  /** External (different origin) — render with a small external icon. */
  external?: boolean;
}

const SURFACES: ShowcaseSurface[] = [
  { to: '/preview/dashboard', key: 'dashboard', icon: LayoutDashboard },
  { to: '/preview/roster', key: 'roster', icon: Users },
  { to: '/preview/client-detail', key: 'client_detail', icon: User },
  { to: '/preview/plan-editor', key: 'plan_editor', icon: ClipboardList },
  { to: '/preview/workouts', key: 'workouts', icon: Dumbbell },
  { to: '/preview/measurements', key: 'measurements', icon: Scale },
  { to: '/preview/calendar', key: 'calendar', icon: Calendar },
  { to: '/preview/assistant', key: 'assistant', icon: Sparkles },
  { to: '/preview/client-portal', key: 'client_portal', icon: User },
];

const MARKETING_URL = import.meta.env.VITE_MARKETING_URL ?? 'http://localhost:5174';

export function Showcase() {
  const { t } = useTranslation('common');

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">
      <header className="mb-10 space-y-3 text-center">
        <h1 className="text-balance text-4xl font-bold tracking-tight md:text-5xl">
          {t('showcase.title')}
        </h1>
        <p className="mx-auto max-w-2xl text-balance text-muted-foreground">
          {t('showcase.subtitle')}
        </p>
        <p className="mx-auto inline-block rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          {t('showcase.demo_mode_note')}
        </p>
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        {SURFACES.map((s) => (
          <SurfaceCard key={s.to} surface={s} />
        ))}
        <ExternalSurfaceCard
          href={`${MARKETING_URL}#playground`}
          title={t('showcase.surfaces.playground.title')}
          description={t('showcase.surfaces.playground.description')}
          icon={Sparkles}
        />
      </section>

      <footer className="mt-12 flex flex-col items-center gap-2 text-center text-sm text-muted-foreground">
        <p>{t('showcase.footer_note')}</p>
        <Link to="/" className="text-primary hover:underline">
          {t('showcase.back_to_landing')} →
        </Link>
      </footer>
    </div>
  );
}

function SurfaceCard({ surface }: { surface: ShowcaseSurface }) {
  const { t } = useTranslation('common');
  const Icon = surface.icon;
  const root = `showcase.surfaces.${surface.key}`;
  return (
    <Link to={surface.to} className="group block">
      <Card
        className={cn(
          'h-full transition-all',
          'hover:border-primary hover:shadow-lg hover:shadow-primary/10',
        )}
      >
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <Icon className="h-4 w-4 text-primary" />
              {t(`${root}.kicker`)}
            </div>
            <CardTitle className="text-lg">{t(`${root}.title`)}</CardTitle>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
        </CardHeader>
        <CardContent className="space-y-3">
          <CardDescription className="text-sm">{t(`${root}.description`)}</CardDescription>
          <div className="text-[10px] font-mono text-muted-foreground">{surface.to}</div>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {(t(`${root}.highlights`, { returnObjects: true }) as string[]).map((h) => (
              <li key={h} className="flex items-start gap-1.5">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-primary" />
                {h}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </Link>
  );
}

function ExternalSurfaceCard({
  href,
  title,
  description,
  icon: Icon,
}: {
  href: string;
  title: string;
  description: string;
  icon: typeof Sparkles;
}) {
  const { t } = useTranslation('common');
  return (
    <a href={href} target="_blank" rel="noreferrer" className="group block">
      <Card className="h-full transition-all hover:border-primary hover:shadow-lg hover:shadow-primary/10">
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <Icon className="h-4 w-4 text-primary" />
              {t('showcase.surfaces.playground.kicker')}
            </div>
            <CardTitle className="text-lg">{title}</CardTitle>
          </div>
          <ExternalLink className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
        </CardHeader>
        <CardContent className="space-y-3">
          <CardDescription className="text-sm">{description}</CardDescription>
          <div className="text-[10px] font-mono text-muted-foreground">{href}</div>
        </CardContent>
      </Card>
    </a>
  );
}
